import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

const valueOrDash = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const formatMoney = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "-";
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const confidenceColor = (confidence) => {
  if (confidence === "high") return "success";
  if (confidence === "medium") return "info";
  if (confidence === "low") return "warning";
  return "default";
};

function RoleContext({ title, role, liveRole }) {
  if (!role) {
    return (
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, height: "100%" }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="body2" color="text.secondary">No linked listing context.</Typography>
      </Box>
    );
  }

  const demand = role.store_demand || {};
  const signal = role.demand_signal || {};
  const syncedQty = role.last_synced_ebay_quantity;
  const liveQty = liveRole?.status === "success" ? liveRole.available_quantity : null;
  const baselineQty = role.decision_quantity;
  const baselineSource = role.decision_quantity_source === "trusted_live_refresh"
    ? "Trusted live eBay"
    : role.decision_quantity_source === "fresh_store_cache"
      ? "Fresh store cache"
      : "Refresh required";

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, height: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Chip size="small" label={role.listing_status || "No listing"} />
        {role.ebay_listing_status && <Chip size="small" variant="outlined" label={`eBay ${role.ebay_listing_status}`} />}
      </Stack>
      <Grid container spacing={0.75}>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Local target</Typography><Typography variant="body2"><strong>{role.local_target_quantity ?? 0}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Price</Typography><Typography variant="body2"><strong>{formatMoney(role.listing_price)}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Last synced eBay qty</Typography><Typography variant="body2"><strong>{valueOrDash(syncedQty)}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Live store qty</Typography><Typography variant="body2"><strong>{liveRole ? valueOrDash(liveQty) : "Not refreshed"}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Sold 30d</Typography><Typography variant="body2"><strong>{demand.sales_30_listing_units ?? 0}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Sold 90d</Typography><Typography variant="body2"><strong>{demand.sales_90_listing_units ?? 0}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Orders 30d</Typography><Typography variant="body2"><strong>{demand.orders_30 ?? 0}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Decision baseline</Typography><Typography variant="body2"><strong>{valueOrDash(baselineQty)}</strong> <Typography component="span" variant="caption" color="text.secondary">({baselineSource})</Typography></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Stock cover</Typography><Typography variant="body2"><strong>{signal.stock_cover_days == null ? "-" : `${signal.stock_cover_days}d`}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">~30d demand target</Typography><Typography variant="body2"><strong>{signal.target_30d_listing_units ?? 0}</strong></Typography></Grid>
        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Replenishment gap</Typography><Typography variant="body2"><strong>{signal.replenishment_gap_listing_units ?? 0}</strong></Typography></Grid>
        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Last sale</Typography><Typography variant="body2">{demand.last_sale_at ? `${formatDate(demand.last_sale_at)}${demand.days_since_last_sale == null ? "" : ` (${demand.days_since_last_sale}d ago)`}` : "No matched sale"}</Typography></Grid>
        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Last local eBay sync</Typography><Typography variant="body2">{formatDate(role.last_ebay_sync_at)}</Typography></Grid>
        {title === "Playset" && role.listing_price != null && (
          <Grid item xs={12}><Typography variant="caption" color="text.secondary">Effective price/card</Typography><Typography variant="body2">{formatMoney(role.effective_price_per_physical_card)}</Typography></Grid>
        )}
      </Grid>
      {syncedQty !== null && syncedQty !== undefined && Number(syncedQty) !== Number(role.local_target_quantity) && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Local target {role.local_target_quantity ?? 0} differs from last-synced eBay quantity {syncedQty}. Bulk Intake uses the trusted/fresh eBay quantity for replenishment; local target is diagnostic only.
        </Alert>
      )}
      {liveRole?.status === "success" && Number(liveQty) !== Number(role.local_target_quantity) && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Live store quantity {liveQty} differs from local target {role.local_target_quantity ?? 0}. Bulk Intake replenishment uses live store quantity {liveQty}; the local target does not block the action.
        </Alert>
      )}
      {role.decision_quantity_source === "refresh_required" && liveRole?.status !== "success" && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Refresh Store Qty before relying on a replenishment recommendation. Local target is not used as the listing baseline.
        </Alert>
      )}
      {liveRole?.status === "error" && <Alert severity="warning" sx={{ mt: 1 }}>{liveRole.message || "Live quantity lookup failed"}</Alert>}
      {liveRole?.status === "unavailable" && <Alert severity="info" sx={{ mt: 1 }}>{liveRole.message}</Alert>}
    </Box>
  );
}

export default function TrainerAllocationDecisionPanel({
  context,
  loading,
  error,
  liveRefresh,
  liveRefreshing,
  onRefreshStoreQty,
  onUseSuggestion,
}) {
  if (loading) {
    return (
      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading current allocation and store demand…</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="warning">Allocation decision context could not be loaded: {error}</Alert>;
  }

  if (!context) {
    return null;
  }

  if (context.match_status !== "matched_existing") {
    return (
      <Alert severity="info">
        No existing Trainer Inventory allocation was found for this card/variant. Manual allocation remains available.
      </Alert>
    );
  }

  const allocation = context.local_allocation || {};
  const suggestion = context.suggestion || {};
  const single = context.roles?.single;
  const playset = context.roles?.playset;
  const liveRoles = liveRefresh?.roles || {};
  const canUseSuggestion =
    Number(suggestion.suggested_single_allocation || 0) > 0 ||
    Number(suggestion.suggested_playset_allocation || 0) > 0;

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1">Allocation Decision Context</Typography>
          <Typography variant="caption" color="text.secondary">
            Imported store orders load automatically. Replenishment uses trusted/fresh eBay store quantity; local targets are shown only as diagnostics.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onRefreshStoreQty} disabled={liveRefreshing}>
          {liveRefreshing ? "Refreshing…" : "Refresh Store Qty"}
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip label={`On hand ${allocation.quantity_on_hand ?? 0}`} />
        <Chip label={`Singles ${allocation.target_single_quantity ?? 0}`} />
        <Chip label={`Playsets ${allocation.target_playset_quantity ?? 0}`} />
        <Chip label={`Reserved ${allocation.reserved_physical_units ?? 0}`} />
        <Chip label={`Unallocated ${allocation.unallocated_physical_units ?? 0}`} />
        {allocation.over_allocated_physical_units > 0 && (
          <Chip color="warning" variant="outlined" label={`Local mismatch ${allocation.over_allocated_physical_units}`} />
        )}
      </Stack>

      {liveRefresh?.error && <Alert severity="warning" sx={{ mb: 1.5 }}>{liveRefresh.error}</Alert>}

      {liveRefresh?.refreshed_at && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Live store quantity refreshed at {formatDate(liveRefresh.refreshed_at)}. eBay was not changed; only the local eBay quantity cache/sync metadata was refreshed.
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <RoleContext title="Single" role={single} liveRole={liveRoles.single} />
        </Grid>
        <Grid item xs={12} md={6}>
          <RoleContext title="Playset" role={playset} liveRole={liveRoles.playset} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2">Suggested incoming allocation</Typography>
              <Chip size="small" color={confidenceColor(suggestion.confidence)} label={`Confidence: ${suggestion.confidence || "none"}`} />
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>+{suggestion.suggested_single_allocation ?? 0} singles</strong> / <strong>+{suggestion.suggested_playset_allocation ?? 0} playsets</strong> / {suggestion.suggested_unallocated_quantity ?? 0} cards unallocated
            </Typography>
            <Typography variant="caption" color="text.secondary">{suggestion.explanation}</Typography>
          </Box>
          <Button size="small" variant="contained" onClick={onUseSuggestion} disabled={!canUseSuggestion}>
            Use Suggestion
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
